export default function Pricing() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const plans = [
    {
      name: 'Starter',
      price: '59',
      description: 'Aloittelijoille ja pienille yrityksille',
      features: [
        'Perusarvostelukyselyt',
        'Sähköpostilähetykset',
        'Google-arvosteluohjaus',
        'Perustuki sähköpostilla',
        'Maksimissaan 50 kyselyä/kk'
      ],
      highlighted: false
    },
    {
      name: 'Growth',
      price: '79',
      description: 'Kasvuyrityksille',
      features: [
        'Kaikki Starterin ominaisuudet',
        'Brändätty viestipohja',
        'Seuranta ja raportointi',
        'Prioriteettituki',
        'Maksimissaan 200 kyselyä/kk',
        'Negatiivisen palautteen ohjaus'
      ],
      highlighted: true,
      badge: 'Suosituin'
    },
    {
      name: 'Pro',
      price: '99',
      description: 'Suurille yrityksille',
      features: [
        'Kaikki Growth-ominaisuudet',
        'Rajattomat kyselyt',
        'Monimutkaiset automaatiot',
        'Omistettu tukihenkilö',
        'Mukautetut raportit'
      ],
      highlighted: false
    }
  ];

  return (
    <section id="pricing" className="py-16 md:py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#3B82F6]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#1E3A8A]/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-12 md:mb-16 animate-[fadeInUp_0.8s_ease-out]">
          <div className="inline-block bg-gradient-to-r from-[#3B82F6] to-[#1E3A8A] text-white px-5 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
            Hinnoittelu
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 font-inter">
            Selkeä hinnoittelu ilman yllätyksiä
          </h2>
          <p className="text-lg md:text-xl text-gray-600">
            Valitse yrityksellesi sopiva paketti. Kaikki paketit sisältävät 14 päivän ilmaisen kokeilun.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
          {plans.map((plan, idx) => (
            <div 
              key={idx} 
              className={`bg-white rounded-2xl md:rounded-[2rem] p-6 md:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 animate-[fadeInUp_0.8s_ease-out] border ${
                plan.highlighted 
                  ? 'ring-2 ring-[#1E3A8A] md:scale-105 border-[#1E3A8A]/20 bg-gradient-to-b from-white to-blue-50/30' 
                  : 'border-gray-100 hover:border-[#3B82F6]/30'
              }`}
              style={{ animationDelay: `${idx * 0.15}s` }}
            >
              {plan.badge && (
                <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white px-5 py-2 rounded-full text-sm font-semibold inline-block mb-6 shadow-lg animate-pulse">
                  ⭐ {plan.badge}
                </div>
              )}
              
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{plan.name}</h3>
              <p className="text-gray-600 mb-6 md:mb-8 text-sm md:text-base">{plan.description}</p>
              
              <div className="mb-6 md:mb-8">
                <span className="text-4xl md:text-6xl font-bold text-gray-900">{plan.price}€</span>
                <span className="text-gray-600 ml-2 text-base md:text-lg">/ kuukausi</span>
              </div>

              <button 
                onClick={() => scrollToSection('trial-form')}
                className={`w-full py-4 md:py-5 rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl transform hover:scale-105 ${
                  plan.highlighted 
                    ? 'bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white hover:from-[#1E40AF] hover:to-[#3B82F6]' 
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Aloita nyt ilmaiseksi
              </button>

              <div className="mt-8 md:mt-10 space-y-4 md:space-y-5">
                {plan.features.map((feature, fIdx) => (
                  <div key={fIdx} className="flex items-start gap-3 group">
                    <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-green-600 text-xl md:text-2xl group-hover:scale-125 transition-transform duration-300"></i>
                    </div>
                    <span className="text-gray-700 text-sm md:text-base leading-relaxed">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center animate-[fadeInUp_0.8s_ease-out_0.6s_both]">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 max-w-2xl mx-auto border border-gray-100">
            <p className="text-gray-700 mb-3 text-base md:text-lg font-medium">
              ✨ Kaikki paketit sisältävät 14 päivän ilmaisen kokeilun.
            </p>
            <p className="text-xs md:text-sm text-gray-500">
              Hinnat eivät sisällä arvonlisäveroa. Palvelu on ilmaisen kokeilun jälkeen 12 kk määräajalla.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}